// Typed ACI (Alora Cultural Intelligence) suggestions client.
// Established CHG-1274 (v11; safeFetch fallback for all errors).
// Updated CHG-1322 (v14; safeFetch relaxed — 404 still soft-empty, 5xx/network propagate).
//
// Implements the runtime side of CHG-1250's design contract. Wraps the
// backend GET /api/v1/aci/suggestions endpoint.
//
// Backend status post-CHG-1322: all 5 CHG-1250 surfaces have live handlers
// in src/api/app/services/aci_suggestions_engine.py. 404 should never
// happen in production, but the legacy soft-empty path is retained so
// rolling deploys don't surface error toasts during the window when the
// backend hasn't yet picked up the new endpoint. 5xx / network errors
// now propagate to ACISurface's error branch ("Suggestions unavailable"
// notice) instead of being silently swallowed.
//
// Surface inventory + per-surface contracts: specs/seeds/dashboard-v2/aci-surfaces.yaml (CHG-1250).
// Type shape: mirrors AISuggestion from api/overview.ts so AICallout can
// consume both bundled-fallback + this client's response interchangeably.

import { apiClient } from "./client";

// Mirrors api/overview.ts:AISuggestion (CHG-1238) so AICallout reuses its
// existing render contract.
export type ACISuggestionKind =
  | "campaign_opportunity"
  | "at_risk_donor"
  | "seasonal_template"
  | "kb_entry"
  | "intent_provenance"
  | "schedule_rationale"
  | string;

export interface ACISuggestion {
  id: string;
  kind: ACISuggestionKind;
  title: string;
  body: string;
  cta?: { label: string; route?: string };
}

/** The 5 surface identifiers from aci-surfaces.yaml (CHG-1250). */
export type ACISurfaceId =
  | "overview-suggestions"
  | "campaign-suggested-actions"
  | "donor-aci-notes"
  | "recurring-intent-provenance"
  | "pledge-schedule-rationale";

export interface ACISuggestionsParams {
  surface_id: ACISurfaceId;
  org_id?: string;
  entity_id?: string;
  limit?: number;
}

interface ACISuggestionsResponse {
  items: ACISuggestion[];
  total?: number;
}

/** Fetch ACI suggestions for a given surface.
 *
 *  CHG-1322 v14 contract:
 *  - 200 with envelope or bare array → parsed array (may be empty; that's a
 *    valid "no matching suggestions" empty state, NOT an error).
 *  - 404 → returns []. The /aci/suggestions endpoint is fully shipped post-
 *    CHG-1322 so 404 should never happen in production, but during
 *    rollouts / staging environments an outdated backend may still 404;
 *    we keep the soft-empty fallback for 404 specifically.
 *  - 5xx / network / parse errors → THROW. useACISuggestions surfaces the
 *    error via react-query's `error` state; ACISurface renders the
 *    "Suggestions unavailable" notice (v14 added). Pre-v14 behavior was
 *    to swallow ALL errors via safeFetch — that masked outages as empty
 *    states, which is now incorrect because every surface has a live
 *    backend handler.
 */
export async function fetchACISuggestions(
  params: ACISuggestionsParams,
): Promise<ACISuggestion[]> {
  try {
    const r = await apiClient().get<ACISuggestionsResponse>("/aci/suggestions", {
      params: {
        surface: params.surface_id,
        org_id: params.org_id,
        entity_id: params.entity_id,
        limit: params.limit,
      },
    });
    // Backend may return either `{items, total}` envelope or a bare
    // array depending on shape evolution; tolerate both per CHG-1250
    // §7.2 forward-compat clause.
    const data = r.data as unknown;
    if (Array.isArray(data)) return data as ACISuggestion[];
    if (data && typeof data === "object" && Array.isArray((data as ACISuggestionsResponse).items)) {
      return (data as ACISuggestionsResponse).items;
    }
    return [];
  } catch (err: unknown) {
    // Soft-empty only for 404 (legacy backend missing the endpoint).
    // Everything else propagates so useACISuggestions surfaces an error.
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return [];
    throw err;
  }
}

export const aciApi = {
  fetchACISuggestions,
};
